/**
 * Laravel API から返される error_code を i18next のキーに変換して翻訳するヘルパー。
 *
 * 使い方:
 *   import { getApiErrorMessage } from '../utils/apiError';
 *   const msg = getApiErrorMessage(err, t);
 *
 * - error_code が存在し、対応する翻訳キーがあれば翻訳済み文字列を返す
 * - error_code がない / 翻訳キーが未定義の場合は response.data.message にフォールバック
 * - それもなければ汎用エラーメッセージを返す
 */

/** error_code → i18next キー のマッピング (namespace: apiErrors) */
const ERROR_CODE_MAP = {
  // Auth
  verification_email_sent:    'verificationEmailSent',
  terms_config_error:         'termsConfigError',
  invalid_agreement_docs:     'invalidAgreementDocs',
  missing_required_agreements:'missingRequiredAgreements',
  invalid_credentials:        'invalidCredentials',
  email_unverified:           'emailUnverified',
  resend_token_expired:       'resendTokenExpired',
  already_verified:           'alreadyVerified',
  verification_resent:        'verificationResent',
  account_locked:             'accountLocked',
  password_attempt_locked:    'passwordAttemptLocked',
  logged_out:                 'loggedOut',

  // Purchase / Reservation
  buyer_role_required:        'buyerRoleRequired',
  invalid_price_id:           'invalidPriceId',
  event_unavailable:          'eventUnavailable',
  inventory_error:            'inventoryError',
  invalid_quantity:           'invalidQuantity',
  mixed_events:               'mixedEvents',
  reservation_limit_exceeded: 'reservationLimitExceeded',
  checkout_session_failed:    'checkoutSessionFailed',

  // Ticket / Registration
  already_checked_in:         'alreadyCheckedIn',
  ticket_voided:              'ticketVoided',
  ticket_not_checkinable:     'ticketNotCheckinable',
  checkin_success:            'checkinSuccess',
  not_free_event:             'notFreeEvent',
  event_registration_closed:  'eventRegistrationClosed',
  already_registered:         'alreadyRegistered',
  registration_success:       'registrationSuccess',
  registration_failed:        'registrationFailed',

  // Gallery / Upload
  event_canceled:             'eventCanceled',
  upload_before_event:        'uploadBeforeEvent',
  upload_forbidden:           'uploadForbidden',
  upload_rate_limited:        'uploadRateLimited',
  upload_limit_exceeded:      'uploadLimitExceeded',
  image_not_found:            'imageNotFound',
  delete_forbidden:           'deleteForbidden',

  // Profile
  no_changes:                 'noChanges',
  wrong_current_password:     'wrongCurrentPassword',
  email_changed:              'emailChanged',
  profile_updated:            'profileUpdated',

  // Donation
  donation_not_accepted:      'donationNotAccepted',
  donation_period_closed:     'donationPeriodClosed',
  stripe_not_configured:      'stripeNotConfigured',
  donation_failed:            'donationFailed',

  // Venue / Event / Seller
  map_zoom_required:          'mapZoomRequired',
  keyword_required:           'keywordRequired',
  keyword_too_long:           'keywordTooLong',
  keyword_invalid_chars:      'keywordInvalidChars',
  stripe_onboarding_required: 'stripeOnboardingRequired',
  terms_consent_required:     'termsConsentRequired',
  venue_not_found:            'venueNotFound',
  forbidden:                  'forbidden',
};

/**
 * @param {unknown} err - axios catch の err
 * @param {function} t  - useTranslation('apiErrors') の t 関数
 * @returns {string}
 */
export function getApiErrorMessage(err, t) {
  const errorCode = err?.response?.data?.error_code;
  const fallbackMessage = err?.response?.data?.message || err?.response?.data?.error;

  if (errorCode && ERROR_CODE_MAP[errorCode]) {
    const key = ERROR_CODE_MAP[errorCode];
    const translated = t(key, { defaultValue: '' });
    if (translated) return translated;
  }

  return fallbackMessage || t('unknown');
}
